import { Button, EmailField, FetchingButton, FieldLegend, FieldSet, email as emailSchema } from '@glade-ai/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { UseFormProps } from 'react-hook-form';
import z from 'zod';

import { ToastTypeVariants } from '@/context/ToastContext';
import { useToast } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import requestPasswordReset from '@/tsClient/auth/requestPasswordReset';

import { type View } from '../types';

type Props = {
  onChangeView: (view: View) => void;
  userEmail: string;
  creatorSlug?: string;
  reEnterPassword?: boolean;
};

// react-hook-form schema
const forgotPasswordSchema = z.object({
  email: emailSchema(),
});

type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>;

const ForgotPasswordForm: React.FC<Props> = ({ onChangeView, userEmail, creatorSlug, reEnterPassword }) => {
  const addToast = useToast();
  const router = useRouter();

  const formConfig = useMemo(
    () =>
      ({
        defaultValues: { email: userEmail || '' },
        mode: 'onBlur',
        resolver: zodResolver(forgotPasswordSchema),
      }) satisfies UseFormProps<ForgotPasswordSchema>,
    [userEmail],
  );

  const { control, handleSubmit } = useForm<ForgotPasswordSchema>(formConfig);
  const { getData: requestPasswordResetFn, fetchingState: requestingPasswordReset } =
    useNoodleApi(requestPasswordReset);

  const onSubmit = async ({ email }: ForgotPasswordSchema): Promise<void> => {
    const returnTo = reEnterPassword ? `/auth/logout?returnTo=${encodeURIComponent(router.asPath)}` : router.asPath;
    await requestPasswordResetFn({ creatorSlug, email, returnTo });
    onChangeView('signIn');
    addToast(ToastTypeVariants.SUCCESS, 'The link to reset your password has been sent.');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FieldSet>
        <FieldLegend className='heading-sm'>Reset password</FieldLegend>
        <span className='body-sm'>We’ll send a link to reset your password to the email you provide.</span>
        <Controller
          name='email'
          control={control}
          render={({ field, fieldState }) => <EmailField field={field} fieldState={fieldState} />}
        />
        <FetchingButton
          variant='primary'
          type='submit'
          size='lg'
          fetching={requestingPasswordReset.isFetching}
          className='w-full'
        >
          Reset Password
        </FetchingButton>
        <p className='text-sm'>
          Meant to try again or create an account?{' '}
          <Button variant='link' className='!text-[--color-primary] px-0.5' onClick={() => onChangeView('signIn')}>
            Sign in
          </Button>{' '}
          or{' '}
          <Button variant='link' className='!text-[--color-primary] px-0.5' onClick={() => onChangeView('signUp')}>
            sign up
          </Button>
          .
        </p>
      </FieldSet>
    </form>
  );
};

export default ForgotPasswordForm;
